import { Injectable } from '@angular/core';
import { EMPTY, Observable, defer } from 'rxjs';
import { catchError, map, retryWhen, switchMap, delay, take, tap } from 'rxjs/operators';
import { CustomApiModels } from '@mona/api';
import { TokenStorageService } from '@mona/auth';
import { ConfigService } from '@mona/config';
import { DataUpdateMessage, DataUpdateMessageModel } from '@mona/models';
import { WsExtendableService } from '@mona/shared/utils';
import { transformApiDataUpdateMessage } from './transforms/data-update.transforms';

/**
 * PDMS updates ws service - stream listens for Encounter, Ward, Bed update messages
 */
@Injectable({ providedIn: 'root' })
export class PdmsUpdatesWsService extends WsExtendableService<
    CustomApiModels.DataUpdateMessage,
    DataUpdateMessage<DataUpdateMessageModel>
> {
    private readonly maxRetries = 5;
    private readonly retryDelay = 2000;

    /**
     * Constructor
     *
     * @param configService ConfigService
     * @param tokenStorage
     */
    constructor(private configService: ConfigService, private tokenStorage: TokenStorageService) {
        super();
    }

    /**
     * Connects to ws for encounter updates with simple retry logic
     */
    connect(): Observable<DataUpdateMessage<DataUpdateMessageModel>> {
        return defer(() => {
            const accessToken = this.tokenStorage.getAccessToken();
            const url = `${this.configService.get('api.coreWsUrl')}/ws/pdms/general/`;
            this.setConfig(url, accessToken);
            return super.connect();
        }).pipe(
            map(message => transformApiDataUpdateMessage(message as CustomApiModels.DataUpdateMessage)),
            retryWhen(errors =>
                errors.pipe(
                    tap(error => console.warn('WebSocket connect failed, retrying...', error)),
                    delay(this.retryDelay),
                    take(this.maxRetries),
                ),
            ),
            catchError(() => EMPTY),
        );
    }
}
